import axios from "./axios";

export type FeedbackTypeValue = 0 | 1 | 2 | 3;
export type FeedbackSourceTypeValue = 0 | 1 | 2 | 3 | 4;
export type FeedbackStatusValue = 0 | 1 | 2 | 3;

export interface AdminFeedbackFilters {
  page?: number;
  pageSize?: number;
  status?: FeedbackStatusValue;
  type?: FeedbackTypeValue;
  sourceType?: FeedbackSourceTypeValue;
  search?: string;
  createdByUserId?: string;
  assignedToUserId?: string;
}

export interface AdminFeedbackTicketListItem {
  id: string;
  title: string;
  type: FeedbackTypeValue;
  status: FeedbackStatusValue;
  sourceType: FeedbackSourceTypeValue;
  sourceSummary?: string;
  creatorName: string;
  createdByUserId: string;
  assignedToUserId?: string;
  assignedToName?: string;
  commentCount: number;
  priority: number;
  createdAtUtc: string;
  updatedAtUtc?: string;
  isHelpful: boolean;
  isLocked?: boolean;
}

export interface AdminFeedbackComment {
  id: string;
  authorUserId: string;
  authorName: string;
  authorAvatarUrl?: string;
  isModeratorNote: boolean;
  message: string;
  createdAtUtc: string;
}

export interface AdminFeedbackDetail extends AdminFeedbackTicketListItem {
  description: string;
  comments: AdminFeedbackComment[];
}

export interface AdminFeedbackListResponse {
  total: number;
  page: number;
  pageSize: number;
  items: AdminFeedbackTicketListItem[];
}

export async function fetchAdminFeedbackTickets(filters: AdminFeedbackFilters): Promise<AdminFeedbackListResponse> {
  const params = new URLSearchParams();
  if (filters.page) params.set("page", String(filters.page));
  if (filters.pageSize) params.set("pageSize", String(filters.pageSize));
  if (typeof filters.status === "number") params.set("status", String(filters.status));
  if (typeof filters.type === "number") params.set("type", String(filters.type));
  if (typeof filters.sourceType === "number") params.set("sourceType", String(filters.sourceType));
  if (filters.search) params.set("search", filters.search);
  if (filters.createdByUserId) params.set("createdByUserId", filters.createdByUserId);
  if (filters.assignedToUserId) params.set("assignedToUserId", filters.assignedToUserId);

  const response = await axios.get<AdminFeedbackListResponse>(`/admin/feedback?${params.toString()}`);
  return response.data;
}

export async function fetchAdminFeedbackDetail(id: string): Promise<AdminFeedbackDetail> {
  const response = await axios.get<AdminFeedbackDetail>(`/admin/feedback/${id}`);
  return response.data;
}

export async function postAdminFeedbackComment(ticketId: string, message: string) {
  const response = await axios.post(`/admin/feedback/${ticketId}/comments`, { message });
  return response.data as AdminFeedbackComment;
}

export interface AdminFeedbackStatusPayload {
  status: FeedbackStatusValue;
  assignedToUserId?: string;
  priority?: number;
  note?: string;
}

export async function updateAdminFeedbackStatus(ticketId: string, payload: AdminFeedbackStatusPayload) {
  const response = await axios.post(`/admin/feedback/${ticketId}/status`, payload);
  return response.data as { success: boolean };
}

export async function markFeedbackHelpful(ticketId: string, isHelpful: boolean) {
  const response = await axios.post(`/admin/feedback/${ticketId}/helpful`, { isHelpful });
  return response.data as { success: boolean };
}

export async function setAdminFeedbackLock(ticketId: string, isLocked: boolean, note?: string) {
  const response = await axios.post(`/admin/feedback/${ticketId}/lock`, { isLocked, note });
  return response.data as { success: boolean; isLocked?: boolean };
}
